/**
 * @module AdapterDetailPage
 * @package @geenius/adapters/react-ant
 * @description Renders the single-domain adapter detail view for the React
 * variant. The page surfaces current provider health, available providers, and
 * inline configuration for one adapter domain using Ant Design tokens.
 */

import type { AdapterDomain } from "@geenius/adapters";
import {
  DOMAIN_DESCRIPTIONS,
  DOMAIN_ICONS,
  DOMAIN_LABELS,
  getProvidersForDomain,
} from "@geenius/adapters";
import theme from "antd/lib/theme/index.js";
import type { JSX } from "react";
import { useState } from "react";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { useAdapterStatus, useAdapters } from "../hooks";

/**
 * Props for rendering the React single-domain adapter detail page.
 *
 * @property domain Adapter domain whose provider details should be displayed.
 * @property onBack Optional callback for returning to the dashboard view.
 * @property className Optional class name merged into the page container.
 * @property style Optional inline styles merged into the page container.
 */
export interface AdapterDetailPageProps {
  domain: AdapterDomain;
  onBack?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Renders the React adapter detail page for one adapter domain.
 *
 * @param props Page props including the selected domain and optional back-navigation handler.
 * @returns The detail view with provider health, available providers, and configuration affordances.
 */
export function AdapterDetailPage({
  domain,
  onBack,
  className,
  style,
}: AdapterDetailPageProps): JSX.Element {
  const { isLoading } = useAdapters();
  const status = useAdapterStatus(domain);
  const [isEditing, setIsEditing] = useState(false);
  const providers = getProvidersForDomain(domain);
  const { token } = theme.useToken();

  if (isLoading) {
    return (
      <div
        className={className}
        style={{
          margin: "0 auto",
          maxWidth: 768,
          display: "flex",
          flexDirection: "column",
          gap: token.marginLG,
          ...style,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: token.marginMD,
          }}
        >
          <div
            style={{
              height: 24,
              width: 128,
              borderRadius: token.borderRadiusSM,
              backgroundColor: token.colorFillAlter,
            }}
          />
          <div
            style={{
              height: 160,
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorBorderSecondary}`,
              backgroundColor: token.colorBgContainer,
            }}
          />
          <div
            style={{
              height: 240,
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorBorderSecondary}`,
              backgroundColor: token.colorBgContainer,
            }}
          />
        </div>
      </div>
    );
  }

  const getTierTokens = (tier: string) => {
    switch (tier) {
      case "pronto":
        return { bg: token.colorPrimaryBg, text: token.colorText };
      case "lancio":
        return { bg: token.colorWarningBg, text: token.colorText };
      default:
        return { bg: token.colorBgLayout, text: token.colorTextSecondary };
    }
  };

  return (
    <div
      className={className}
      style={{
        margin: "0 auto",
        maxWidth: 768,
        display: "flex",
        flexDirection: "column",
        gap: token.marginXXL,
        color: token.colorText,
        ...style,
      }}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: token.fontSizeSM,
            color: token.colorTextSecondary,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            transition: "color 0.2s",
            alignSelf: "flex-start",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = token.colorText)}
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = token.colorTextSecondary)
          }
        >
          <span>←</span> Back to Adapters
        </button>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: token.marginLG,
        }}
      >
        <div
          style={{
            display: "flex",
            height: 64,
            width: 64,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorder}`,
            backgroundColor: token.colorBgContainer,
            fontSize: 32,
            boxShadow: token.boxShadowTertiary,
          }}
        >
          {DOMAIN_ICONS[domain]}
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: token.marginSM,
            }}
          >
            <h1
              style={{
                fontSize: token.fontSizeHeading2,
                fontWeight: 800,
                color: token.colorTextHeading,
                margin: 0,
              }}
            >
              {DOMAIN_LABELS[domain]}
            </h1>
            <AdapterStatusBadge status={status.status} />
          </div>
          <p
            style={{
              marginTop: 4,
              fontSize: token.fontSizeSM,
              color: token.colorTextSecondary,
              margin: 0,
            }}
          >
            {DOMAIN_DESCRIPTIONS[domain]}
          </p>
        </div>
      </div>

      <div
        style={{
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorder}`,
          backgroundColor: token.colorBgContainer,
          padding: token.paddingLG,
          boxShadow: token.boxShadowTertiary,
        }}
      >
        <h2
          style={{
            marginBottom: token.marginMD,
            fontSize: 12,
            fontWeight: 600,
            textTransform: "uppercase",
            color: token.colorTextSecondary,
            margin: "0 0 16px 0",
          }}
        >
          Current Status
        </h2>
        <div
          style={{
            display: "grid",
            gap: token.marginMD,
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          }}
        >
          <div>
            <p
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                color: token.colorTextSecondary,
                margin: 0,
              }}
            >
              Provider
            </p>
            <p
              style={{
                marginTop: 2,
                fontSize: token.fontSizeSM,
                fontWeight: 600,
                color: token.colorText,
                margin: 0,
              }}
            >
              {status.provider !== "none" ? status.provider : "None"}
            </p>
          </div>
          <div>
            <p
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                color: token.colorTextSecondary,
                margin: 0,
              }}
            >
              Status
            </p>
            <div style={{ marginTop: 2 }}>
              <AdapterStatusBadge status={status.status} />
            </div>
          </div>
          <div>
            <p
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                color: token.colorTextSecondary,
                margin: 0,
              }}
            >
              Last Checked
            </p>
            <p
              style={{
                marginTop: 2,
                fontSize: token.fontSizeSM,
                color: token.colorTextSecondary,
                margin: 0,
              }}
            >
              {status.lastCheckedAt
                ? new Date(status.lastCheckedAt).toLocaleTimeString()
                : "Not checked"}
            </p>
          </div>
        </div>

        {status.error && (
          <div
            style={{
              marginTop: token.marginMD,
              borderRadius: token.borderRadiusSM,
              border: `1px solid ${token.colorErrorBorder}`,
              backgroundColor: token.colorErrorBg,
              padding: "12px 16px",
              fontSize: token.fontSizeSM,
              color: token.colorText,
            }}
          >
            <span style={{ fontWeight: 600 }}>Error:</span> {status.error}
          </div>
        )}

        {status.latency && (
          <div style={{ marginTop: token.marginMD }}>
            <p
              style={{
                marginBottom: 4,
                fontSize: 10,
                textTransform: "uppercase",
                color: token.colorTextSecondary,
                margin: "0 0 4px 0",
              }}
            >
              Latency
            </p>
            <div
              style={{
                height: 8,
                width: "100%",
                overflow: "hidden",
                borderRadius: 4,
                backgroundColor: token.colorFillAlter,
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 4,
                  transition: "all 0.2s",
                  backgroundColor:
                    status.latency < 100
                      ? token.colorSuccess
                      : status.latency < 500
                        ? token.colorWarning
                        : token.colorError,
                  width: `${Math.min(100, (status.latency / 1000) * 100)}%`,
                }}
              />
            </div>
            <p
              style={{
                marginTop: 4,
                fontSize: 12,
                color: token.colorTextSecondary,
                margin: 0,
              }}
            >
              {status.latency}ms
            </p>
          </div>
        )}
      </div>

      <div
        style={{
          borderRadius: token.borderRadiusLG,
          border: `1px solid ${token.colorBorder}`,
          backgroundColor: token.colorBgContainer,
          padding: token.paddingLG,
          boxShadow: token.boxShadowTertiary,
        }}
      >
        <div
          style={{
            marginBottom: token.marginMD,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "uppercase",
              color: token.colorTextSecondary,
              margin: 0,
            }}
          >
            Available Providers
          </h2>
          <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
            {providers.length} providers
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {providers.map((provider, idx) => {
            const tierTokens = getTierTokens(provider.tier);
            return (
              <div
                key={provider.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 0",
                  borderTop:
                    idx === 0
                      ? "none"
                      : `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <div>
                  <p
                    style={{
                      fontSize: token.fontSizeSM,
                      fontWeight: 600,
                      color: token.colorText,
                      margin: 0,
                    }}
                  >
                    {provider.name}
                  </p>
                  <p
                    style={{
                      fontSize: 12,
                      color: token.colorTextSecondary,
                      margin: 0,
                    }}
                  >
                    {provider.description}
                  </p>
                </div>
                <span
                  style={{
                    alignSelf: "flex-start",
                    borderRadius: token.borderRadiusSM,
                    padding: "2px 8px",
                    fontSize: 10,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    backgroundColor: tierTokens.bg,
                    color: tierTokens.text,
                  }}
                >
                  {provider.tier}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        {isEditing ? (
          <AdapterConfigForm
            domain={domain}
            onSave={async () => setIsEditing(false)}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            style={{
              width: "100%",
              borderRadius: token.borderRadiusLG,
              border: `1px dashed ${token.colorBorder}`,
              backgroundColor: token.colorFillQuaternary,
              padding: 24,
              textAlign: "center",
              fontSize: token.fontSizeSM,
              color: token.colorTextSecondary,
              transition: "all 0.2s",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = token.colorPrimaryHover;
              e.currentTarget.style.color = token.colorText;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = token.colorBorder;
              e.currentTarget.style.color = token.colorTextSecondary;
            }}
          >
            Configure {DOMAIN_LABELS[domain]} Adapter
          </button>
        )}
      </div>
    </div>
  );
}
